/* eslint-disable id-length */
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { isEmpty } from 'lodash';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import { resolveConflict as dashboardResolve } from '@actions/dashboard-actions';
import { resolveConflict as groupsResolve } from '@actions/groups-actions';
import * as dialog from '@constants/dialogs';
import Button from '@material-ui/core/Button';
import { Checkbox } from '@mui';
import { OVERLAP_STATUS } from '@utils/data-detail/conflicts';
import { isAppAdmin } from '@utils/permission-utils';
import { sortBy } from '@utils/shared-utils';
import './actions-resolve-box.scss';

const MAX_NOTES_LEN = 5000;

const ResolveBox = ({ entity, onClose }) => {
  const dispatch = useDispatch();

  const dialogParams = useSelector(state => state.dashboard.dialogParams[dialog.OVERLAP_STATUS]);
  const loading = useSelector(state => state.dashboard.resolution.loading);
  const overlapResolutionReasons = useSelector(state => state.dataTypes.overlap_resolution_reason);

  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState([]);
  const [bothSides, setBothSides] = useState(false);

  const onCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const onResolve = useCallback(() => {
    const payload = {
      ...dialogParams,
      notes,
      reason,
      bothSides
    };
    // Dispatch action to resolve the conflict on the backend:
    if (location.pathname.startsWith('/group/')) {
      dispatch(groupsResolve(payload));
    } else {
      dispatch(dashboardResolve(payload));
    }
    setNotes('');
    setReason([]);
    setBothSides(false);
    onClose();
  }, [bothSides, dialogParams, dispatch, notes, onClose, reason]);

  const onSelectChange = useCallback(event => setReason(event.target.value), []);

  const onTextChange = useCallback(event => setNotes(event.target.value.substring(0, MAX_NOTES_LEN)), []);

  const disableBothSidesCheck = useCallback((entityArg, overlapId) => {
    if (!entityArg || !overlapId || !isAppAdmin()) {
      return true;
    }
    const overlap = R.find(R.propEq(overlapId, 'overlap'))(entityArg.overlap_status);
    return !overlap || overlap.overlap_status !== OVERLAP_STATUS.new;
  }, []);

  const onCheck = useCallback(() => setBothSides(prev => !prev), []);

  const getReasons = useCallback(() => {
    return sortBy(R.values(overlapResolutionReasons), 'name');
  }, [overlapResolutionReasons]);

  if (loading) {
    return null;
  }

  const reasons = getReasons();

  return (
    <div styleName="resolve-box-form-container">
      <div styleName="title">
        Resolve Conflict
      </div>
      <div styleName="resolve-box-form">
        <div styleName="controls">
          <div styleName="field">
            <FormControl fullWidth style={{ marginTop: '1rem' }}>
              <InputLabel shrink={reason.length > 0}>
                How was this conflict resolved?
              </InputLabel>
              <Select
                onChange={onSelectChange}
                value={reason}
                multiple
              >
                {reasons.map(r => <MenuItem key={r.name} value={r.name}>{r.name}</MenuItem>)}
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
            <div styleName="field-footer">
              <div styleName="hint">
                {reason.length > 0 ? '' : 'You can select multiple.'}
              </div>
              <div styleName="counter" />
            </div>
          </div>
          <div styleName="field">
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              onChange={onTextChange}
              value={notes}
              InputLabelProps={{ shrink: Boolean(notes) }}
              style={{ marginTop: '1rem' }}
            />
            <div styleName="field-footer">
              <div styleName="hint" />
              <div styleName="counter">
                {notes ? notes.length : 0}/{MAX_NOTES_LEN}
              </div>
            </div>
          </div>
        </div>
        <div styleName="footer">
          <div styleName="footer-grow">
            {isAppAdmin() && (
              <Checkbox
                checked={bothSides}
                disabled={disableBothSidesCheck(entity, dialogParams.overlapId)}
                label="Resolve the conflict from both sides."
                onChange={onCheck}
              />
            )}
          </div>
          <div styleName="footer-button">
            <Button onClick={onCancel}>CANCEL</Button>
            <Button
              color="primary"
              disabled={isEmpty(reason)}
              onClick={onResolve}
              style={{ marginLeft: '0.5rem' }}
              variant="contained"
            >
              SAVE
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

ResolveBox.propTypes = {
  entity: PropTypes.object,
  onClose: PropTypes.func
};

export default ResolveBox;
